import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal, SectionLabel, Counter } from "@/components/site/bits";
import { Slider } from "@/components/ui/slider";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { TrendingUp, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function Calculator() {
  const [leads, setLeads] = useState([120]);
  const [revenue, setRevenue] = useState([500000]);
  const [team, setTeam] = useState([8]);
  const [show, setShow] = useState(false);
  const [email, setEmail] = useState("");

  const l = leads[0], r = revenue[0], tm = team[0];
  const projLeads = Math.round(l * 1.85);
  const projRevenue = Math.round(r * 2.4);
  const hoursSaved = Math.round(tm * 32);
  const roi = Math.round(((projRevenue - r) / (r || 1)) * 100);

  const generate = async () => {
    setShow(true);
    if (email) {
      try {
        await api.post("/leads", { name: "Calculator Lead", email, source: "calculator", meta: { leads: l, revenue: r, team: tm, projRevenue } });
        toast.success("ROI report saved — our team will reach out!");
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail));
      }
    }
  };

  return (
    <section id="calculator" className="relative py-28 px-6" data-testid="calculator">
      <div className="mx-auto max-w-6xl">
        <Reveal>
          <SectionLabel>Business Growth Calculator</SectionLabel>
          <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight max-w-2xl">
            See your <span className="text-gradient">growth potential</span> with AI
          </h2>
        </Reveal>

        <div className="mt-12 grid lg:grid-cols-2 gap-6">
          <Reveal>
            <div className="rounded-2xl glass p-8 space-y-8" data-testid="calc-inputs">
              <SliderRow label="Monthly Leads" value={l} min={10} max={2000} step={10} onChange={setLeads} format={(v) => v.toLocaleString()} />
              <SliderRow label="Monthly Revenue (₹)" value={r} min={50000} max={10000000} step={50000} onChange={setRevenue} format={(v) => `₹${v.toLocaleString()}`} />
              <SliderRow label="Team Size" value={tm} min={1} max={200} step={1} onChange={setTeam} format={(v) => v} />

              <div>
                <label className="text-xs uppercase tracking-widest text-white/50">Email (to receive report)</label>
                <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" data-testid="calc-email"
                  className="mt-2 bg-white/5 border-white/10 focus-visible:ring-[#00E5FF]" />
              </div>
              <button onClick={generate} data-testid="calc-generate"
                className="w-full rounded-full bg-gradient-to-r from-[#00E5FF] to-[#7B61FF] py-3.5 font-semibold text-[#0B1220] hover:scale-[1.02] transition-transform inline-flex items-center justify-center gap-2">
                <Sparkles className="h-4 w-4" /> Generate ROI Report
              </button>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="relative rounded-2xl glass p-8 h-full overflow-hidden" data-testid="calc-report">
              <div className="absolute -right-20 -top-20 h-52 w-52 rounded-full bg-[#00FFB2]/10 blur-3xl" />
              <div className="flex items-center gap-2 text-sm text-white/60"><TrendingUp className="h-4 w-4 text-[#00FFB2]" /> Projected with 9nocAI automation</div>
              <AnimatePresence>
                {show ? (
                  <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mt-8 space-y-6">
                    <Metric label="Projected Monthly Leads" value={<Counter to={projLeads} suffix="/mo" />} />
                    <Metric label="Projected Monthly Revenue" value={<Counter to={projRevenue} prefix="₹" />} />
                    <Metric label="Team Hours Saved / Month" value={<Counter to={hoursSaved} suffix=" hrs" />} />
                    <div className="rounded-xl bg-[#00FFB2]/10 border border-[#00FFB2]/20 p-5">
                      <div className="text-xs text-white/60">Estimated ROI Uplift</div>
                      <div className="font-display text-4xl font-bold text-[#00FFB2] mt-1"><Counter to={roi} suffix="%" /></div>
                    </div>
                  </motion.div>
                ) : (
                  <div className="mt-16 text-center text-white/40" data-testid="calc-empty">
                    <Sparkles className="mx-auto h-10 w-10 mb-4 text-white/20" />
                    Adjust the sliders and generate your personalized AI growth report.
                  </div>
                )}
              </AnimatePresence>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

const SliderRow = ({ label, value, min, max, step, onChange, format }) => (
  <div>
    <div className="flex items-center justify-between mb-3">
      <span className="text-sm text-white/70">{label}</span>
      <span className="font-display font-semibold text-[#00E5FF]">{format(value)}</span>
    </div>
    <Slider value={[value]} min={min} max={max} step={step} onValueChange={onChange} />
  </div>
);

const Metric = ({ label, value }) => (
  <div className="flex items-center justify-between border-b border-white/5 pb-4">
    <span className="text-sm text-white/60">{label}</span>
    <span className="font-display text-2xl font-bold text-gradient">{value}</span>
  </div>
);
